"use client";

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { format, subDays } from "date-fns";
import { Loader2, Mail, Megaphone, AlertCircle, CheckCircle2, Clock } from "lucide-react";

export function HistoryTab() {
  // Fetch last 30 days of logs
  const { data: logs, isLoading } = useQuery({
    queryKey: ['mass-communication-logs', 'history'],
    queryFn: async () => {
      const thirtyDaysAgo = subDays(new Date(), 30).toISOString();
      
      const { data, error } = await supabase
        .from('mass_communication_logs')
        .select('*')
        .gte('created_at', thirtyDaysAgo)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data || [];
    }
  });

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'rfq_digest':
        return <Mail className="h-4 w-4" />;
      case 'custom_announcement':
        return <Megaphone className="h-4 w-4" />;
      default:
        return <Mail className="h-4 w-4" />;
    }
  };

  const getTypeBadge = (type: string) => {
    switch (type) {
      case 'rfq_digest':
        return <Badge variant="outline">RFQ Digest</Badge>;
      case 'custom_announcement':
        return <Badge variant="secondary">Announcement</Badge>;
      default:
        return <Badge variant="outline">{type}</Badge>;
    }
  };

  const getMethodBadge = (method: string) => {
    switch (method) {
      case 'scheduled':
        return <Badge variant="outline" className="text-xs"><Clock className="h-3 w-3 mr-1" />Scheduled</Badge>;
      case 'manual':
        return <Badge className="text-xs">Manual</Badge>;
      case 'test':
        return <Badge variant="secondary" className="text-xs">Test</Badge>;
      default:
        return <Badge variant="outline" className="text-xs">{method}</Badge>;
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Execution History</CardTitle>
        <CardDescription>
          Last 30 days of mass communication runs
        </CardDescription>
      </CardHeader>
      <CardContent>
        {logs && logs.length > 0 ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Type</TableHead>
                <TableHead>Method</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Target Date</TableHead>
                <TableHead className="text-right">Recipients</TableHead>
                <TableHead className="text-right">Sent</TableHead>
                <TableHead className="text-right">Failed</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {logs.map((log) => {
                const hasErrors = log.failed_sends > 0;
                const isComplete = log.completed_at !== null;
                
                return (
                  <TableRow key={log.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {getTypeIcon(log.communication_type)}
                        {getTypeBadge(log.communication_type)}
                      </div>
                    </TableCell>
                    <TableCell>{getMethodBadge(log.trigger_method)}</TableCell>
                    <TableCell className="text-sm">
                      {format(new Date(log.created_at), 'MMM d, yyyy h:mm a')}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {log.target_date ? format(new Date(log.target_date), 'MMM d, yyyy') : '—'}
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      {log.total_recipients}
                    </TableCell>
                    <TableCell className="text-right text-green-600 font-medium">
                      {log.successful_sends}
                    </TableCell>
                    <TableCell className="text-right text-destructive font-medium">
                      {log.failed_sends}
                    </TableCell>
                    <TableCell>
                      {isComplete ? (
                        hasErrors ? (
                          <Badge variant="destructive" className="text-xs">
                            <AlertCircle className="h-3 w-3 mr-1" />
                            Errors
                          </Badge>
                        ) : (
                          <Badge variant="default" className="text-xs bg-green-600">
                            <CheckCircle2 className="h-3 w-3 mr-1" />
                            Complete
                          </Badge>
                        )
                      ) : (
                        <Badge variant="secondary" className="text-xs">
                          <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                          Running
                        </Badge>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        ) : (
          <div className="text-center py-12 text-muted-foreground">
            No communication logs found in the last 30 days.
          </div>
        )}
      </CardContent>
    </Card>
  );
}
